import React, { Suspense, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import PageConfig from '@/component/layout/PageConfig.tsx';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CloseButton from '@/component/button/CloseButton.tsx';
import { GoogleMap, MarkerF, InfoWindowF, CircleF, PolylineF } from '@react-google-maps/api';
import useQueryGetUserDetail from '@/api/auth/getUser.ts';
import { IMarker, IMarkerInfo, MarketTYPE } from '@/pages/dashboard/DashboardMapDefault.tsx';
import { AlertStatus, DeviceStatus, IUser } from '@/api/user/getUsers.ts';
import useIsMount from '@/util/hook/useIsMount.ts';
import ReactDOMServer from 'react-dom/server';
import MarkerWindow from '@/pages/dashboard/MarkerWindow.tsx';
import HomeIcon from '@/assets/ic_map_house.svg?react';
import useQueryGetMe from '@/api/auth/getMe.ts';
import useQueryGetUserDailyPosition, { IUserPosition, IDateAvailability, checkDateHasData } from '@/api/user/getUserDailyPosition.ts';
import { format, isToday, isBefore, startOfDay, eachDayOfInterval, endOfMonth, startOfMonth, subDays, addDays } from 'date-fns';
import { IUserBbox } from '@/api/user/getUsersBbox';
import { useTranslation } from 'react-i18next';

// 커스텀 달력 컴포넌트
const CustomDatePicker: React.FC<{
    value: string;
    onChange: (date: string) => void;
    availableDates: IDateAvailability[];
    isLoadingAvailability: boolean;
    onMonthChange: (month: Date) => void;
}> = ({ value, onChange, availableDates, isLoadingAvailability, onMonthChange }) => {
    const { t } = useTranslation();
    const [showCalendar, setShowCalendar] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const buttonRef = useRef<HTMLButtonElement>(null);

    // 월이 변경될 때마다 부모 컴포넌트에 알림
    useEffect(() => {
        onMonthChange(currentMonth);
    }, [currentMonth, onMonthChange]);

    // 해당 날짜에 데이터가 있는지 확인
    const isDateAvailable = (date: string) => {
        const availability = availableDates.find(item => item.date === date);
        return availability?.hasData || false;
    };

    // 해당 날짜가 로딩 중인지 확인
    const isDateLoading = (date: string) => {
        const availability = availableDates.find(item => item.date === date);
        return availability?.isLoading || false;
    };

    // 달력에 표시할 날짜들 생성
    const calendarDates = useMemo(() => {
        const start = startOfMonth(currentMonth);
        const end = endOfMonth(currentMonth);
        const firstWeek = subDays(start, start.getDay());
        const lastWeek = addDays(end, 6 - end.getDay());
        
        return eachDayOfInterval({ start: firstWeek, end: lastWeek });
    }, [currentMonth]);
    
    // 이전/다음 달로 이동
    const goToPreviousMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
    };

    const goToNextMonth = () => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
    };

    // 날짜 클릭 핸들러
    const handleDateClick = (date: Date) => {
        const dateString = format(date, 'yyyy-MM-dd');
        const isFuture = isBefore(new Date(), startOfDay(date)) && !isToday(date);
        
        if (!isFuture && isDateAvailable(dateString)) {
            onChange(dateString);
            setShowCalendar(false);
        }
    };

    // 날짜 스타일 계산
    const getDateStyle = (date: Date) => {
        const dateString = format(date, 'yyyy-MM-dd');
        const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
        const isFuture = isBefore(new Date(), startOfDay(date)) && !isToday(date);
        const isSelected = value === dateString;
        const hasData = isDateAvailable(dateString);
        const isLoading = isDateLoading(dateString);
        
        let baseStyle = 'w-8 h-8 text-sm rounded-full flex items-center justify-center cursor-pointer relative ';
        
        if (!isCurrentMonth) {
            baseStyle += 'text-gray-300 ';
        } else if (isFuture) {
            baseStyle += 'text-gray-300 cursor-not-allowed ';
        } else if (isSelected) {
            baseStyle += 'bg-blue-500 text-white ';
        } else if (isLoading) {
            baseStyle += 'text-gray-400 ';
        } else if (hasData) {
            baseStyle += 'text-black hover:bg-blue-100 ';
        } else {
            baseStyle += 'text-gray-400 cursor-not-allowed ';
        }
        
        return baseStyle;
    };

    // 달력 위치 계산
    const getCalendarPosition = () => {
        if (!buttonRef.current) return 'left-0';
        
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const calendarWidth = 280; // 달력의 최소 너비
        
        // 버튼의 오른쪽 끝에서 달력 너비만큼 더한 값이 화면을 벗어나는지 확인
        if (buttonRect.left + calendarWidth > windowWidth) {
            return 'right-0'; // 오른쪽 정렬
        }
        
        return 'left-0'; // 왼쪽 정렬 (기본)
    };

    return (
        <div className="relative">
            <button
                ref={buttonRef}
                onClick={() => setShowCalendar(!showCalendar)}
                className="p-2 border rounded-lg text-sm bg-white min-w-[120px] text-left"
            >
                {value || t('users.detail.mapPage.datePicker.placeholder')}
            </button>
            
            {showCalendar && (
                <div className={`absolute top-full ${getCalendarPosition()} mt-1 bg-white border rounded-lg shadow-lg z-50 p-4 min-w-[280px] max-w-[320px]`}>
                    {/* 달력 헤더 */}
                    <div className="flex justify-between items-center mb-4">
                        <button onClick={goToPreviousMonth} className="p-1 hover:bg-gray-100 rounded">
                            ←
                        </button>
                        <h3 className="font-medium">
                            {format(currentMonth, t('users.detail.mapPage.datePicker.monthFormat'))}
                        </h3>
                        <button onClick={goToNextMonth} className="p-1 hover:bg-gray-100 rounded">
                            →
                        </button>
                    </div>
                    
                    {/* 요일 헤더 */}
                    <div className="grid grid-cols-7 gap-1 mb-2">
                        {[t('common.dayOfWeek.sun'), t('common.dayOfWeek.mon'), t('common.dayOfWeek.tue'), t('common.dayOfWeek.wed'), t('common.dayOfWeek.thu'), t('common.dayOfWeek.fri'), t('common.dayOfWeek.sat')].map(day => (
                            <div key={day} className="text-center text-xs text-gray-500 py-1">
                                {day}
                            </div>
                        ))}
                    </div>
                    
                    {/* 날짜 그리드 */}
                    <div className="grid grid-cols-7 gap-1">
                        {calendarDates.map((date, index) => {
                            const dateString = format(date, 'yyyy-MM-dd');
                            const isLoading = isDateLoading(dateString);
                            
                            return (
                                <div
                                    key={index}
                                    className={getDateStyle(date)}
                                    onClick={() => handleDateClick(date)}
                                >
                                    {date.getDate()}
                                    {isLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="w-1 h-1 bg-blue-500 rounded-full animate-pulse"></div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {/* 범례 */}
                    <div className="mt-3 pt-3 border-t text-xs">
                        <div className="flex justify-between">
                            <span className="text-black">● {t('users.detail.mapPage.datePicker.legend.dataExists')}</span>
                            <span className="text-gray-400">● {t('users.detail.mapPage.datePicker.legend.noData')}</span>
                        </div>
                    </div>
                    
                    {isLoadingAvailability && (
                        <div className="mt-2 text-xs text-gray-500 text-center">
                            {t('users.detail.mapPage.datePicker.legend.loading')}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export interface IProps {}

const UserDetailMapPage: React.FC<IProps> = ({}) => {
    const { t } = useTranslation();
    const params = useParams();
    const userID = Number(params?.id);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDate = searchParams.get('date') || format(new Date(), 'yyyy-MM-dd');
    const { data } = useQueryGetUserDetail(userID);
    const { data: me } = useQueryGetMe();
    const { data: dailyPositions, isLoading: isLoadingPositions, isError, error, refetch: refetchPositions } = useQueryGetUserDailyPosition(userID, selectedDate);
    const [userInfo, setUserInfo] = useState<any>(null);
    const { isMount } = useIsMount();
    const [showDailyTrack, setShowDailyTrack] = useState<boolean>(true);
    const [positionsErrorMsg, setPositionsErrorMsg] = useState<string>(t('locationHistory.error.noData'));
    const [selectedPosition, setSelectedPosition] = useState<IUserPosition | null>(null);
    
    // 위치 재생 관련 상태
    const [isPlaying, setIsPlaying] = useState<boolean>(false);
    const [currentPositionIndex, setCurrentPositionIndex] = useState<number>(0);
    const [playbackSpeed, setPlaybackSpeed] = useState<number>(1); // 재생 속도 (1x, 2x, 4x)
    const playbackTimerRef = useRef<number | null>(null);
    const [sortedPositions, setSortedPositions] = useState<IUserPosition[]>([]);

    // 날짜별 데이터 존재 여부 상태 및 캐시
    const [availableDates, setAvailableDates] = useState<IDateAvailability[]>([]);
    const [isLoadingAvailability, setIsLoadingAvailability] = useState<boolean>(false);
    const [currentCalendarMonth, setCurrentCalendarMonth] = useState<Date>(new Date());
    const dateCache = useRef<Map<string, boolean>>(new Map()); // 날짜별 데이터 존재 여부 캐시

    // 특정 월의 모든 날짜에 대해 데이터 존재 여부 확인
    const checkMonthDataAvailability = useCallback(async (month: Date) => {
        if (!userID || userID <= 0) return;

        setIsLoadingAvailability(true);
        
        try {
            // 현재 달력에 표시되는 모든 날짜 생성
            const start = startOfMonth(month);
            const end = endOfMonth(month);
            const firstWeek = subDays(start, start.getDay());
            const lastWeek = addDays(end, 6 - end.getDay());
            const dates = eachDayOfInterval({ start: firstWeek, end: lastWeek });
            
            // 초기 상태 설정 (모든 날짜를 로딩 중으로 표시)
            const initialDates = dates.map(date => ({
                date: format(date, 'yyyy-MM-dd'),
                hasData: false,
                isLoading: true
            }));
            setAvailableDates(initialDates);
            
            // 각 날짜별로 데이터 존재 여부 확인
            const checkPromises = dates.map(async (date) => {
                const dateString = format(date, 'yyyy-MM-dd');
                
                // 캐시된 데이터가 있으면 사용
                if (dateCache.current.has(dateString)) {
                    return {
                        date: dateString,
                        hasData: dateCache.current.get(dateString) || false,
                        isLoading: false
                    };
                }
                
                // 캐시된 데이터가 없으면 API 호출
                const hasData = await checkDateHasData(userID, dateString);
                dateCache.current.set(dateString, hasData);
                
                return {
                    date: dateString,
                    hasData,
                    isLoading: false
                };
            });
            
            // 모든 날짜 확인이 완료되면 상태 업데이트
            const results = await Promise.all(checkPromises);
            setAvailableDates(results);
        } catch (error) {
            console.error('날짜별 데이터 확인 중 오류:', error);
            
            // 에러 발생 시 모든 날짜를 데이터 없음으로 표시
            const start = startOfMonth(month);
            const end = endOfMonth(month);
            const firstWeek = subDays(start, start.getDay());
            const lastWeek = addDays(end, 6 - end.getDay());
            const dates = eachDayOfInterval({ start: firstWeek, end: lastWeek });
            
            const errorDates = dates.map((date: Date) => ({
                date: format(date, 'yyyy-MM-dd'),
                hasData: false,
                isLoading: false
            }));
            setAvailableDates(errorDates);
        } finally {
            setIsLoadingAvailability(false);
        }
    }, [userID]);

    // 달력 월 변경 핸들러
    const handleMonthChange = useCallback((month: Date) => {
        setCurrentCalendarMonth(month);
        checkMonthDataAvailability(month);
    }, [checkMonthDataAvailability]);

    // 날짜 선택 핸들러
    const handleDateSelect = (date: string) => {
        const params = new URLSearchParams(searchParams);
        params.set('date', date);
        setSearchParams(params);
    };

    // 컴포넌트 마운트 시 현재 월 데이터 확인
    useEffect(() => {
        if (userID > 0) {
            checkMonthDataAvailability(currentCalendarMonth);
        }
    }, [userID, currentCalendarMonth, checkMonthDataAvailability]);

    // 일일 위치 데이터 처리 및 정렬된 위치 데이터 설정
    useEffect(() => {
        if (isError && error instanceof Error) {
            setPositionsErrorMsg(error.message);
        } else if (isLoadingPositions) {
            setPositionsErrorMsg(t('common.loadingData'));
        } else if (!dailyPositions || dailyPositions.length === 0) {
            setPositionsErrorMsg(t('users.detail.mapPage.error.noDataToday'));
        } else {
            setPositionsErrorMsg("");
            // 시간순으로 정렬된 위치 데이터 설정
            const sorted = [...dailyPositions].sort((a, b) => {
                return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
            });
            setSortedPositions(sorted);
            // 재생 중단 및 인덱스 초기화
            setIsPlaying(false);
            setCurrentPositionIndex(0);
        }
    }, [isError, error, isLoadingPositions, dailyPositions]);

    // 재생 속도에 따른 지연 시간 계산 (밀리초)
    const getDelayBySpeed = (speed: number): number => {
        switch (speed) {
            case 1: return 1000;  // 1x = 1초
            case 2: return 500;   // 2x = 0.5초
            case 4: return 250;   // 4x = 0.25초
            default: return 1000; // 기본 1초
        }
    };

    // 재생 시작 함수
    const startPlayback = () => {
        if (sortedPositions.length === 0) return;
        
        // 이미 재생 중이면 중지
        if (isPlaying) {
            stopPlayback();
            return;
        }
        
        // 재생 시작
        setIsPlaying(true);
        
        // 마지막 위치라면 처음으로 돌아가기
        if (currentPositionIndex >= sortedPositions.length - 1) {
            setCurrentPositionIndex(0);
        }
        
        // 타이머 시작
        playNext();
    };
    
    // 다음 위치로 이동하는 함수
    const playNext = () => {
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
        }
        
        if (currentPositionIndex < sortedPositions.length - 1) {
            // 속도에 따른 고정 지연 시간 사용
            const delay = getDelayBySpeed(playbackSpeed);
            
            // 다음 위치로 이동
            playbackTimerRef.current = window.setTimeout(() => {
                setCurrentPositionIndex(prevIndex => prevIndex + 1);
                
                // 마지막 위치가 아니면 계속 재생
                if (currentPositionIndex < sortedPositions.length - 2) {
                    playNext();
                } else {
                    // 마지막 위치에 도달하면 재생 중단
                    setIsPlaying(false);
                }
            }, delay);
        } else {
            // 마지막 위치에 도달하면 재생 중단
            setIsPlaying(false);
        }
    };
    
    // 재생 중지 함수
    const stopPlayback = () => {
        setIsPlaying(false);
        if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    };
    
    // 재생 속도 변경 함수
    const changePlaybackSpeed = () => {
        // 1x -> 2x -> 4x -> 1x 순환
        setPlaybackSpeed(prevSpeed => {
            const newSpeed = prevSpeed === 4 ? 1 : prevSpeed * 2;
            
            // 재생 중이라면 현재 재생 중단 후 새 속도로 재시작
            if (isPlaying) {
                stopPlayback();
                setTimeout(() => {
                    setIsPlaying(true);
                    playNext();
                }, 100);
            }
            
            return newSpeed;
        });
    };
    
    // 컴포넌트 언마운트 시 타이머 정리
    useEffect(() => {
        return () => {
            if (playbackTimerRef.current) {
                window.clearTimeout(playbackTimerRef.current);
            }
        };
    }, []);
    
    // 재생 상태가 변경될 때 타이머 업데이트
    useEffect(() => {
        if (isPlaying) {
            playNext();
        } else if (playbackTimerRef.current) {
            window.clearTimeout(playbackTimerRef.current);
            playbackTimerRef.current = null;
        }
    }, [isPlaying]);

    const openUserInfo = (info?: IMarker<IUser>) => {
        if (!info || userInfo?.id === info?.id) {
            setUserInfo(undefined);
        } else {
            const { latitude = 0, longitude = 0 } = info?.location || {};

            // IUser를 IUserBbox 형태로 변환
            const convertToUserBbox = (users: IUser[]): IUserBbox[] => {
                return users.map(user => ({
                    id: user.id,
                    name: user.name,
                    location: user.location ? {
                        latitude: user.location.latitude,
                        longitude: user.location.longitude,
                        timestamp: user.location.timestamp,
                        accuracy: user.location.accuracy
                    } : undefined,
                    todayAlert: user.alertStatus || AlertStatus.NONE,
                    deviceStatus: user.device?.status || DeviceStatus.OFFLINE
                }));
            };

            if (latitude === 0 && longitude === 0) {
                const infoWithoutAddress: IMarkerInfo<IUserBbox> = {
                    ...info!,
                    // locationName: 'No location',
                    address: 'Coordinates not available',
                    users: convertToUserBbox(info.users),
                };

                setUserInfo(infoWithoutAddress);
                return;
            }

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode(
                { location: { lat: latitude, lng: longitude } },
                (results, status) => {
                    if (status === 'OK' && results && results[0]) {
                        const infoWithAddress: IMarkerInfo<IUserBbox> = {
                            ...info!,
                            // locationName: results[1]?.formatted_address || '',
                            address: results[0].formatted_address,
                            users: convertToUserBbox(info.users),
                        };

                        setUserInfo(infoWithAddress);
                    } else {
                        const infoWithoutAddress: IMarkerInfo<IUserBbox> = {
                            ...info!,
                            // locationName: 'Unknown location',
                            address: 'Geocoding failed',
                            users: convertToUserBbox(info.users),
                        };

                        setUserInfo(infoWithoutAddress);
                    }
                }
            );
        }
    };

    // 위치 포인트 클릭 핸들러
    const handlePositionClick = (position: IUserPosition) => {
        setSelectedPosition(position);
    };

    // 시간 포매팅 함수
    const formatTimestamp = (timestamp: string) => {
        try {
            const date = new Date(timestamp);
            return date.toLocaleString('ko-KR', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit', 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit',
                hour12: true 
            });
        } catch (error) {
            return timestamp;
        }
    };

    const getMarkerColor = (alertStatus: AlertStatus | undefined) => {
        let color = '#2966F4'; // 기본값
        switch (alertStatus) {
            case AlertStatus.EMERGENCY:
                color = '#FF003D';
                break;
            case AlertStatus.BIO:
                color = '#FF7D33';
                break;
            default:
                color = '#2966F4';
                break;
        }
        return color;
    };

    const marker = useMemo(() => {
        if (!data) return undefined;
        return {
            id: Number(params?.id),
            deviceStatus: data?.device?.status,
            location: {
                latitude: data?.location?.latitude,
                longitude: data?.location?.longitude,
                accuracy: data?.location?.accuracy,
                timestamp: data?.location?.timestamp,
            },
            type: MarketTYPE.SINGLE,
            alertStatus: data?.alertStatus as AlertStatus ?? AlertStatus.NONE, // `AlertStatus` Enum으로 변환
            users: [data],
        };
    }, [data]);

    // 현재 재생 중인 위치
    const currentPlaybackPosition = useMemo(() => {
        return isPlaying && sortedPositions.length > 0 && currentPositionIndex < sortedPositions.length
            ? sortedPositions[currentPositionIndex]
            : null;
    }, [isPlaying, sortedPositions, currentPositionIndex]);

    const dailyPositionPath = useMemo(() => {
        if (sortedPositions.length === 0) return [];
        
        return sortedPositions.map(pos => ({
            lat: pos.latitude,
            lng: pos.longitude
        }));
    }, [sortedPositions]);

    // 위치 경로 스타일 
    const polylineOptions = {
        strokeColor: '#2966F4',
        strokeOpacity: 0.8,
        strokeWeight: 3,
    };


    const mapOptions = {
        // disableDefaultUI: true,  // 기본 UI 비활성화
        // zoomControl: false,      // 줌 컨트롤 비활성화
        // mapTypeControl: false,   // 지도 유형 선택 비활성화
        // streetViewControl: false, // 스트리트 뷰 컨트롤 비활성화
        fullscreenControl: false // 풀스크린 컨트롤 비활성화
    };

    // X 버튼 클릭 핸들러
    const handleClose = () => {
        // 바로 사용자 상세 페이지로 이동
        navigate(`/user?id=${userID}`);
    };

    return (
        <PageConfig showTopNav={true} showSideNav={true}>
            <div className={'w-full h-full relative'}>
                <Suspense fallback={null}>
                    {isMount && data && (
                        <div style={{ width: '100%', height: '100%' }}>
                            <GoogleMap
                                center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                zoom={15}
                                mapContainerStyle={{ width: '100%', height: '100%' }}
                                options={mapOptions}
                            >
                                {isMount && data?.location && (
                                    <>
                                        <CircleF
                                            center={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            radius={data?.location.accuracy}
                                            options={{
                                                fillColor: '#78bfffcc',
                                                strokeColor: '#78bfffcc',
                                                strokeWeight: 1,
                                            }}
                                        />
                                        <MarkerF
                                            position={{ lat: data?.location?.latitude || 0, lng: data?.location?.longitude || 0 }}
                                            icon={{
                                                url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                    `
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 50 50">
                                                        <circle cx="25" cy="25" r="20" fill="${getMarkerColor(data?.alertStatus)}" stroke="white" stroke-width="3"/>
                                                    </svg>
                                                    `
                                                )}`,
                                                scaledSize: new google.maps.Size(40, 40),
                                            }}
                                            onClick={() => openUserInfo(marker)}
                                        />

                                        {isMount && data?.addressLatitude && data?.addressLongitude && (
                                            <MarkerF
                                                position={{ lat: data?.addressLatitude ?? 0, lng: data?.addressLongitude ?? 0 }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        ReactDOMServer.renderToString(<HomeIcon />)
                                                    )}`,
                                                    scaledSize: new google.maps.Size(25, 25),
                                                }}
                                            />
                                        )}

                                        {/* 하루 동안의 위치 경로 표시 */}
                                        {showDailyTrack && dailyPositionPath.length > 1 && (
                                            <PolylineF
                                                path={dailyPositionPath}
                                                options={polylineOptions}
                                            />
                                        )}

                                        {/* 위치 이동 경로의 각 포인트 표시 */}
                                        {showDailyTrack && !isPlaying && sortedPositions.map((pos, index) => (
                                            <MarkerF
                                                key={`daily-pos-${index}`}
                                                position={{ lat: pos.latitude, lng: pos.longitude }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
                                                            <circle cx="8" cy="8" r="6" fill="#2966F4" stroke="white" stroke-width="2"/>
                                                        </svg>`
                                                    )}`,
                                                    scaledSize: new google.maps.Size(12, 12),
                                                }}
                                                opacity={0.8}
                                                onClick={() => handlePositionClick(pos)}
                                            />
                                        ))}
                                        
                                        {/* 재생 중인 위치 마커 (크고 눈에 띄게) */}
                                        {currentPlaybackPosition && (
                                            <MarkerF
                                                key="playback-marker"
                                                position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                                icon={{
                                                    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                                        `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
                                                            <circle cx="15" cy="15" r="10" fill="#FF5722" stroke="white" stroke-width="3"/>
                                                        </svg>`
                                                    )}`,
                                                    scaledSize: new google.maps.Size(30, 30),
                                                }}
                                                animation={google.maps.Animation.BOUNCE}
                                            />
                                        )}
                                    </>
                                )}

                                {userInfo && (
                                    <InfoWindowF
                                        position={{ lat: userInfo?.location?.latitude || 0, lng: userInfo?.location?.longitude || 0 }}
                                        onCloseClick={() => setUserInfo(undefined)}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <MarkerWindow info={userInfo} isMasking={me?.email === 'dbd_cm@clomental.com'} />
                                        </div>
                                    </InfoWindowF>
                                )}

                                {/* 선택된 위치 포인트 정보창 */}
                                {selectedPosition && (
                                    <InfoWindowF
                                        position={{ lat: selectedPosition.latitude, lng: selectedPosition.longitude }}
                                        onCloseClick={() => setSelectedPosition(null)}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <p className="font-semibold mb-1">{t('users.detail.mapPage.infoWindow.position.title')}</p>
                                            <p className="text-sm">{t('common.time')} {formatTimestamp(selectedPosition.timestamp)}</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.position.accuracy')} {selectedPosition.accuracy}m</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.position.order')} {selectedPosition.order}</p>
                                        </div>
                                    </InfoWindowF>
                                )}
                                
                                {/* 현재 재생 중인 위치 정보창 */}
                                {currentPlaybackPosition && !selectedPosition && (
                                    <InfoWindowF
                                        position={{ lat: currentPlaybackPosition.latitude, lng: currentPlaybackPosition.longitude }}
                                    >
                                        <div style={{ fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 400 }}>
                                            <p className="font-semibold mb-1">{t('users.detail.mapPage.infoWindow.playback.title')}</p>
                                            <p className="text-sm">{t('common.time')} {formatTimestamp(currentPlaybackPosition.timestamp)}</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.position.accuracy')} {currentPlaybackPosition.accuracy}m</p>
                                            <p className="text-sm">{t('users.detail.mapPage.infoWindow.playback.progress')} {currentPositionIndex + 1}/{sortedPositions.length}</p>
                                        </div>
                                    </InfoWindowF>
                                )}
                            </GoogleMap>
                        </div>
                    )}
                </Suspense>
                
                {/* 상단 컨트롤 바 */}
                <div className="absolute top-0 left-0 right-0 bg-white shadow-md z-20 p-4">
                    <div className="flex flex-col gap-3">
                        {/* 첫 번째 줄: 사용자 이름과 날짜 선택 */}
                        <div className="flex items-center gap-2">
                            <span className="text-gray-600">{t('common.user')} </span>
                            <span className="font-medium">{data?.name || t('common.loading')}</span>
                        </div>

                        {/* 두 번째 줄: 컨트롤 버튼들 */}
                        <div className="flex items-center gap-2">
                            <CustomDatePicker
                                value={selectedDate}
                                onChange={handleDateSelect}
                                availableDates={availableDates}
                                isLoadingAvailability={isLoadingAvailability}
                                onMonthChange={handleMonthChange}
                            />
                            <button 
                                className="bg-blue-500 text-white px-3 py-2 rounded text-sm"
                                onClick={() => refetchPositions()}
                            >
                                {t('common.button.refresh')}
                            </button>
                            
                            {!isLoadingPositions && sortedPositions.length > 0 && (
                                <>
                                    <button 
                                        className={`${isPlaying ? 'bg-red-500' : 'bg-green-500'} text-white px-3 py-2 rounded text-sm`}
                                        onClick={startPlayback}
                                    >
                                        {isPlaying ? t('common.button.stop') : t('common.button.play')}
                                    </button>
                                    
                                    <button
                                        className="bg-gray-200 px-3 py-2 rounded text-sm"
                                        onClick={changePlaybackSpeed}
                                    >
                                        {playbackSpeed}x
                                    </button>
                                    
                                    {currentPlaybackPosition && (
                                        <div className="bg-white px-3 py-2 rounded border text-sm">
                                            {currentPositionIndex + 1}/{sortedPositions.length}
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {!isLoadingPositions && dailyPositions && dailyPositions.length > 0 && (
                                <button 
                                    className={`${showDailyTrack ? 'bg-blue-500 text-white' : 'bg-gray-300 text-gray-700'} px-3 py-2 rounded text-sm`}
                                    onClick={() => setShowDailyTrack(!showDailyTrack)}
                                >
                                    {showDailyTrack ? t('common.button.hideRoute') : t('common.button.showRoute')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* X 버튼 */}
                <CloseButton 
                    className={'absolute top-0 right-0 p-[20px] bg-white m-[20px] z-30 rounded-lg shadow-md hover:bg-gray-100'} 
                    onClick={handleClose} 
                />
                
                {/* 위치 데이터 상태 표시 */}
                {isLoadingPositions && (
                    <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-white p-2 rounded shadow z-10">
                        {t('common.loadingData')}
                    </div>
                )}
                
                {!isLoadingPositions && positionsErrorMsg && (
                    <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-white p-3 rounded shadow z-10 flex flex-col items-center">
                        <div className="text-center">{positionsErrorMsg}</div>
                        <button 
                            className="mt-2 bg-blue-500 text-white px-3 py-1 rounded text-sm"
                            onClick={() => refetchPositions()}
                        >
                            {t('common.button.retry')}
                        </button>
                    </div>
                )}
            </div>
        </PageConfig>
    );
};

export default UserDetailMapPage;